import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import Captcha, { validateCaptcha } from '../Captcha';

const VerificationDialog = ({
  isOpen,
  onClose,
  verificationType,
  loginValue,
  onVerify,
  isVerifying = false,
  error = ''
}) => {
  const [otp, setOtp] = useState('');
  const [internalError, setInternalError] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Reset fields and fetch fresh CAPTCHA when dialog opens
  useEffect(() => {
    if (isOpen) {
      setOtp('');
      setInternalError('');
      setCaptchaInput('');
      setRefreshTrigger(prev => prev + 1);
    }
  }, [isOpen]);

  // Refresh CAPTCHA when external error changes (indicating attempt failed and CAPTCHA expired)
  useEffect(() => {
    if (error) {
      setRefreshTrigger(prev => prev + 1);
      setCaptchaInput('');
    }
  }, [error]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setInternalError('');
  };

  const handleVerify = () => {
    if (!otp.trim()) {
      setInternalError('Please enter OTP');
      return;
    }
    if (otp.length < 6) {
      setInternalError('Please enter a valid 6-digit OTP');
      return;
    }

    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      setInternalError(captchaValidation.error);
      return;
    }

    onVerify(otp, captchaInput, captchaKey);
  };

  const handleClose = () => {
    setOtp('');
    setInternalError('');
    setCaptchaInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Verify {verificationType === 'email' ? 'Email' : 'Phone Number'}
              </h3>
              <p className="text-sm text-gray-600">
                Enter the OTP sent to your {verificationType}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>


        {/* OTP Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter OTP
          </label>
          <input
            type="text"
            autoComplete="off"
            value={otp}
            onChange={handleOtpChange}
            placeholder="Enter 6-digit OTP"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 text-center text-lg tracking-widest ${error ? 'border-red-500' : 'border-gray-300'
              }`}
            maxLength="6"
            disabled={isVerifying}
          />
          {internalError && (
            <div className="flex items-center space-x-1 mt-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-600">{internalError}</p>
            </div>
          )}
          {error && !internalError && (
            <div className="flex items-center space-x-1 mt-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* CAPTCHA for Verification */}
        <div className="mb-6">
          <Captcha
            value={captchaInput}
            onChange={(e) => {
              setCaptchaInput(e.target.value);
              if (internalError) setInternalError('');
            }}
            error={internalError && internalError.includes('CAPTCHA') ? internalError : ''}
            disabled={isVerifying}
            onCaptchaChange={(data) => setCaptchaKey(data.key)}
            refreshTrigger={refreshTrigger}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleClose}
            disabled={isVerifying}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={isVerifying || !otp.trim()}
            className={`flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-semibold transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${isVerifying || !otp.trim()
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-primary-700'
              }`}
          >
            {isVerifying ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationDialog;
